import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Tooltip } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { AutocompleteValueField, InputField } from 'components/form';

import {
  GET_GLOBAL_EQUIPMENT_OPTIONS,
  GlobalEquipmentOptionsResponse,
  GlobalEquipmentOptionsVariables,
} from '../../api';
import { EquipmentStandardSingleInfoFormValues } from '../../model';

export const StandardInfoSingle = () => {
  const { control, setValue } = useFormContext<EquipmentStandardSingleInfoFormValues>();

  const [make, year, model] = useWatch({ control, name: ['make', 'year', 'model'] });

  const { data, previousData, loading } = useQuery<GlobalEquipmentOptionsResponse, GlobalEquipmentOptionsVariables>(
    GET_GLOBAL_EQUIPMENT_OPTIONS,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          year: year ? [year] : [],
          make: make ? [make] : [],
        },
      },
    }
  );

  const source = data ?? previousData;

  const { makeOptions, modelOptions, yearOptions } = useMemo(
    () => ({
      makeOptions: source?.globalEquipment?.result?.entity?.makes ?? [],
      modelOptions: source?.globalEquipment?.result?.entity?.models ?? [],
      yearOptions: source?.globalEquipment?.result?.entity?.years ?? [],
    }),
    [source?.globalEquipment?.result?.entity]
  );

  const isYearMissing = !!year && !loading && !yearOptions.some(({ value }) => value === year);
  const isMakeMissing = !!make && !loading && !makeOptions.some(({ value }) => value === make);
  const isModelMissing = !!model && !loading && !modelOptions.some(({ value }) => value === model);

  return (
    <>
      {isYearMissing ? (
        <InputField
          name="year"
          label="Year"
          control={control}
          inputProps={{
            size: 'small',
            required: true,
            variant: 'outlined',
          }}
        />
      ) : (
        <AutocompleteValueField
          size="small"
          name="year"
          control={control}
          loading={loading}
          options={yearOptions}
          textFieldProps={{
            required: true,
            label: 'Year',
            variant: 'outlined',
          }}
          extraAction={() => {
            setValue('make', null);
            setValue('model', null);
          }}
        />
      )}

      {isMakeMissing ? (
        <InputField
          name="make"
          label="Make"
          control={control}
          inputProps={{
            size: 'small',
            required: true,
            variant: 'outlined',
          }}
        />
      ) : (
        <AutocompleteValueField
          size="small"
          name="make"
          control={control}
          loading={loading}
          options={makeOptions}
          textFieldProps={{
            required: true,
            label: 'Make',
            variant: 'outlined',
          }}
          extraAction={() => {
            setValue('model', null);
          }}
        />
      )}

      {isModelMissing ? (
        <InputField
          name="model"
          label="Model"
          control={control}
          inputProps={{
            size: 'small',
            required: true,
            variant: 'outlined',
          }}
        />
      ) : (
        <Tooltip arrow title={!make ? 'Please select a make first' : ''}>
          <Box>
            <AutocompleteValueField
              size="small"
              name="model"
              disabled={!make}
              control={control}
              loading={loading}
              options={modelOptions}
              textFieldProps={{
                required: true,
                label: 'Model',
                variant: 'outlined',
              }}
            />
          </Box>
        </Tooltip>
      )}
    </>
  );
};
